import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { Plus, Pencil, Trash2, RefreshCw, Save, X, ToggleLeft, ToggleRight, Coins, DollarSign, Clock, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const CAT_OPTIONS = [
  { value: 'starter', label: 'Starter', color: '#10B981' },
  { value: 'mid',     label: 'Mid',     color: '#3B82F6' },
  { value: 'high',    label: 'High',    color: '#8B5CF6' },
  { value: 'whale',   label: 'Whale',   color: '#FBBF24' },
];

const EMPTY = { name: '', fp: 100, cash: 20, cat: 'starter', sort_order: 0, active: true };

export default function MarketplaceItemsPage() {
  const [tab, setTab] = useState('items'); // items | redemptions
  const [items, setItems] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const loadItems = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/admin/marketplace/items');
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const loadRedemptions = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/admin/marketplace/redemptions');
      setRedemptions(r.data?.redemptions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load redemptions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'items') loadItems();
    else loadRedemptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const startNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const startEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it }); setShowForm(true); };
  const cancel = () => { setEditing(null); setForm(EMPTY); setShowForm(false); };

  const save = async () => {
    if (!form.name?.trim()) return toast.error('Name is required');
    if (Number(form.fp) <= 0 || Number(form.cash) <= 0) return toast.error('FP and Cash must be > 0');
    const payload = {
      name: form.name.trim(),
      fp: Number(form.fp),
      cash: Number(form.cash),
      cat: form.cat,
      sort_order: Number(form.sort_order) || 0,
      active: !!form.active,
    };
    try {
      if (editing?.id) {
        await api.put(`/api/admin/marketplace/items/${editing.id}`, payload);
        toast.success('Item updated');
      } else {
        await api.post('/api/admin/marketplace/items', payload);
        toast.success('Item created');
      }
      cancel();
      loadItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const toggle = async (it) => {
    try {
      await api.put(`/api/admin/marketplace/items/${it.id}`, { ...it, active: !it.active });
      loadItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Toggle failed'); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    try {
      await api.delete(`/api/admin/marketplace/items/${it.id}`);
      toast.success('Deleted');
      loadItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-marketplace">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Marketplace — FP → Cash</h1>
          <p className="text-sm text-gray-400 mt-0.5">Configure the redemption tiers and review player redemption history.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => (tab === 'items' ? loadItems() : loadRedemptions())} data-testid="mp-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
          {tab === 'items' && <Button onClick={startNew} data-testid="mp-new-item"><Plus className="w-4 h-4 mr-1" /> New Item</Button>}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/10">
        <button onClick={() => setTab('items')} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'items' ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-transparent text-gray-400 hover:text-white'}`} data-testid="mp-tab-items">Items ({items.length})</button>
        <button onClick={() => setTab('redemptions')} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'redemptions' ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-transparent text-gray-400 hover:text-white'}`} data-testid="mp-tab-redemptions">Redemptions ({redemptions.length})</button>
      </div>

      {/* Form */}
      {showForm && tab === 'items' && (
        <div className="rounded-xl border border-white/10 bg-[#131822] p-5 space-y-4" data-testid="mp-item-form">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{editing ? 'Edit item' : 'New item'}</h3>
            <button onClick={cancel} className="text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-gray-400">Name</label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Quick Cash" data-testid="mp-field-name" />
            </div>
            <div>
              <label className="text-xs text-gray-400">FP cost</label>
              <Input type="number" value={form.fp} onChange={e => setForm({ ...form, fp: e.target.value })} data-testid="mp-field-fp" />
            </div>
            <div>
              <label className="text-xs text-gray-400">Cash payout (USD)</label>
              <Input type="number" value={form.cash} onChange={e => setForm({ ...form, cash: e.target.value })} data-testid="mp-field-cash" />
            </div>
            <div>
              <label className="text-xs text-gray-400">Category</label>
              <select value={form.cat} onChange={e => setForm({ ...form, cat: e.target.value })} className="w-full h-10 rounded-md bg-[#0B0E14] border border-white/10 px-3 text-sm" data-testid="mp-field-cat">
                {CAT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400">Sort order</label>
              <Input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: e.target.value })} />
            </div>
            <label className="flex items-end gap-2 text-sm pb-2">
              <input type="checkbox" checked={!!form.active} onChange={e => setForm({ ...form, active: e.target.checked })} />
              Active
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={cancel}>Cancel</Button>
            <Button onClick={save} data-testid="mp-save"><Save className="w-4 h-4 mr-1" /> {editing ? 'Update' : 'Create'}</Button>
          </div>
        </div>
      )}

      {/* Items table */}
      {tab === 'items' && (
        <div className="rounded-xl border border-white/10 bg-[#131822] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0B0E14] text-gray-400">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-right px-4 py-3">FP cost</th>
                <th className="text-right px-4 py-3">Cash</th>
                <th className="text-right px-4 py-3">Sort</th>
                <th className="text-center px-4 py-3">Active</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="text-center py-6 text-gray-400">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-gray-400">No items yet.</td></tr>}
              {items.map((it, idx) => {
                const cat = CAT_OPTIONS.find(c => c.value === it.cat) || CAT_OPTIONS[0];
                return (
                  <tr key={it.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`mp-row-${idx}`}>
                    <td className="px-4 py-3 font-medium">{it.name}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: `${cat.color}22`, color: cat.color }}>{cat.label}</span></td>
                    <td className="px-4 py-3 text-right"><span className="inline-flex items-center gap-1 text-amber-400"><Coins className="w-3 h-3" />{Number(it.fp).toLocaleString()}</span></td>
                    <td className="px-4 py-3 text-right"><span className="inline-flex items-center gap-1 text-green-400"><DollarSign className="w-3 h-3" />{Number(it.cash).toLocaleString()}</span></td>
                    <td className="px-4 py-3 text-right text-gray-400">{it.sort_order}</td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => toggle(it)} className="text-gray-300 hover:text-white" data-testid={`mp-toggle-${idx}`}>
                        {it.active ? <ToggleRight className="w-6 h-6 text-green-400" /> : <ToggleLeft className="w-6 h-6 text-gray-500" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => startEdit(it)} className="p-1.5 rounded hover:bg-white/10" data-testid={`mp-edit-${idx}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(it)} className="p-1.5 rounded hover:bg-white/10 text-red-400" data-testid={`mp-delete-${idx}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Redemptions list */}
      {tab === 'redemptions' && (
        <div className="rounded-xl border border-white/10 bg-[#131822] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0B0E14] text-gray-400">
              <tr>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Item</th>
                <th className="text-right px-4 py-3">FP</th>
                <th className="text-right px-4 py-3">Cash</th>
                <th className="text-center px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-center py-6 text-gray-400">Loading…</td></tr>}
              {!loading && redemptions.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-gray-400">No redemptions yet.</td></tr>}
              {redemptions.map((r, idx) => (
                <tr key={r.id} className="border-t border-white/5" data-testid={`mp-redemption-${idx}`}>
                  <td className="px-4 py-3 text-gray-300 whitespace-nowrap"><Clock className="w-3 h-3 inline mr-1 opacity-60" />{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-300"><User className="w-3 h-3 inline mr-1 opacity-60" />{(r.user_id || '').slice(0, 8)}…</td>
                  <td className="px-4 py-3">{r.item_name}</td>
                  <td className="px-4 py-3 text-right text-amber-400">{Number(r.fp).toLocaleString()} FP</td>
                  <td className="px-4 py-3 text-right text-green-400">${Number(r.cash).toLocaleString()}</td>
                  <td className="px-4 py-3 text-center"><span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-400">{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
